import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, Upload } from 'lucide-react';
import { AppHeader } from '@/components/AppHeader';
import { useDashboardController } from './dashboard/useDashboardController';
import { useDirectorDashboardFilters } from './dashboard/useDirectorDashboardFilters';
import { TournamentCard } from '@/components/dashboard/TournamentCard';
import { DashboardFilterToolbar } from '@/lib/dashboard-filters';
import { importTrf16 } from '@/lib/api';
import { ApiError } from '@/lib/api/core';
import { showErrorToast, showSuccessToast } from '@/lib/toast';
import { useTranslation } from 'react-i18next';

// Toolbar appears once the dashboard has accumulated enough
// tournaments to actually need it. Below the threshold the
// page renders as it always did — clean list, no chrome.
const TOOLBAR_MIN_TOURNAMENTS = 5;

export function DashboardPage() {
  const { t } = useTranslation('director');
  const navigate = useNavigate();
  const dash = useDashboardController();
  const filters = useDirectorDashboardFilters(dash.tournaments);
  const showToolbar = dash.tournaments.length >= TOOLBAR_MIN_TOURNAMENTS;

  // Story 099 — import a TRF16 file into a new draft tournament.
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const [importing, setImporting] = useState(false);
  async function handleImportTrf16(file: File) {
    setImporting(true);
    try {
      const res = await importTrf16(file);
      showSuccessToast(
        t('dashboard.import_trf16.success', {
          players: res.imported.players,
          rounds: res.imported.rounds,
        }),
      );
      if (res.skipped.length > 0) {
        showErrorToast(
          t('dashboard.import_trf16.skipped', { count: res.skipped.length }),
        );
      }
      navigate(`/tournaments/${res.tournamentId}`);
    } catch (err) {
      showErrorToast(
        err instanceof ApiError ? err.userMessage : t('dashboard.import_trf16.failed'),
      );
    } finally {
      setImporting(false);
      if (importInputRef.current) importInputRef.current.value = '';
    }
  }

  return (
    <div data-testid="dashboard-page" className="min-h-screen bg-background">
      <AppHeader />

      <main className="mx-auto max-w-4xl px-6 py-8">
        <div className="mb-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <h2
            data-testid="dashboard-title"
            className="text-2xl font-semibold"
          >
            {t('dashboard.title')}
          </h2>
          <div className="flex w-full md:w-auto gap-2">
            <input
              ref={importInputRef}
              type="file"
              accept=".trf,.txt"
              data-testid="import-trf16-input"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleImportTrf16(f);
              }}
            />
            <Button
              className="flex-1 md:flex-none"
              size="sm"
              variant="outline"
              data-testid="import-trf16-button"
              disabled={importing}
              onClick={() => importInputRef.current?.click()}
            >
              <Upload className="mr-1 h-4 w-4" />
              {importing
                ? t('dashboard.import_trf16.importing')
                : t('dashboard.import_trf16.button')}
            </Button>
            <Button
              className="flex-1 md:flex-none"
              size="sm"
              data-testid="create-tournament-button"
              onClick={() => navigate('/tournaments/new')}
            >
              <Plus className="mr-1 h-4 w-4" />
              {t('dashboard.new_tournament')}
            </Button>
          </div>
        </div>

        {dash.loading ? (
          <p>{t('dashboard.loading')}</p>
        ) : dash.tournaments.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center gap-4 py-12">
              <p data-testid="empty-state" className="text-muted-foreground">
                {t('dashboard.empty')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {showToolbar && (
              <DashboardFilterToolbar
                filters={filters}
                i18nNamespace="director"
                i18nPrefix="dashboard.toolbar"
              />
            )}
            {filters.filteredItems.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center gap-3 py-12">
                  <p
                    data-testid="dashboard-filter-empty-state"
                    className="text-muted-foreground"
                  >
                    {t('dashboard.toolbar.empty_state')}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="dashboard-clear-filters-button"
                    onClick={filters.clearAll}
                  >
                    {t('dashboard.toolbar.clear_filters')}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {filters.filteredItems.map((tournament) => (
                  <TournamentCard key={tournament.id} tournament={tournament} />
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
